import type { CSSProperties } from 'react';
import { ButtonPreset } from '@/constants/componentPresets';
import { ColorType, getColorValue } from '@/theme/colors';
import { FontWeight } from '@/theme/typography';

const DARK_BUTTON_SHADOW =
  '#ffffffe8 0px 3px 4px -4px inset, #ffffff75 0px -1px 4px -3px inset, #0000007d 1px 1px 4px -1px';
const SECONDARY_BUTTON_CLICK_SHADOW =
  '#00000037 3px 3px 4px 0px inset, #00000052 0px 0px 0px 2px inset';
const EMBOSSED_BUTTON_SHADOW =
  'inset #ffffff69 2px 2px 2px -2px, inset #ffffffb0 -1px -2px 5px -4px, #000000b5 3px 0px 4px 0px';

export interface ButtonPresetConfig {
  bgColor: ColorType;
  bgOpacity: number;
  borderColor: ColorType;
  borderOpacity: number;
  borderWidth: number;
  backdropBlur: number;
  glow: number;
  hoverBgOpacity?: number;
  hoverBorderOpacity?: number;
  hoverGlow?: number;
  clickBgOpacity?: number;
  clickBorderOpacity?: number;
  clickGlow?: number;
  clickShadow?: string;
  textColor: ColorType;
  textWeight: FontWeight;
  textSize?: number;
  customShadow?: {
    regular: string;
    hover: string;
    click: string;
  };
  containerStyle?: CSSProperties;
  glossy?: number;
  hoverTextWeight?: FontWeight;
}

export const BUTTON_PRESET_CONFIGS: Record<ButtonPreset, ButtonPresetConfig> = {
  [ButtonPreset.Primary]: {
    bgColor: ColorType.Accent,
    bgOpacity: 0.3,
    borderColor: ColorType.White,
    borderOpacity: 0.16,
    borderWidth: 1,
    backdropBlur: 1,
    glow: 0,
    hoverBorderOpacity: 0.5,
    hoverGlow: 2,
    clickBgOpacity: 0.37,
    clickBorderOpacity: 0.35,
    clickGlow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.SemiBold,
  },
  [ButtonPreset.Secondary]: {
    bgColor: ColorType.White,
    bgOpacity: 0.11,
    borderColor: ColorType.White,
    borderOpacity: 0.08,
    borderWidth: 1,
    backdropBlur: 1,
    glow: 0,
    hoverBgOpacity: 0.14,
    hoverBorderOpacity: 0.35,
    hoverGlow: 1,
    clickBgOpacity: 0.05,
    clickBorderOpacity: 0,
    clickGlow: 0,
    clickShadow: SECONDARY_BUTTON_CLICK_SHADOW,
    textColor: ColorType.White,
    textWeight: FontWeight.SemiBold,
  },
  [ButtonPreset.Secondary2]: {
    bgColor: ColorType.White,
    bgOpacity: 0.03,
    borderColor: ColorType.White,
    borderOpacity: 0.08,
    borderWidth: 1,
    backdropBlur: 1,
    glow: 0,
    hoverBgOpacity: 0.07,
    hoverBorderOpacity: 0.18,
    hoverGlow: 0,
    clickBgOpacity: 0.1,
    clickBorderOpacity: 0.22,
    clickGlow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.SemiBold,
  },
  [ButtonPreset.Ghost]: {
    bgColor: ColorType.Transparent,
    bgOpacity: 0,
    borderColor: ColorType.Transparent,
    borderOpacity: 0,
    borderWidth: 0,
    backdropBlur: 0,
    glow: 0,
    hoverBgOpacity: 0.20,
    hoverBorderOpacity: 0,
    hoverGlow: 0,
    clickBgOpacity: 0.60,
    clickBorderOpacity: 0,
    clickGlow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.SemiBold,
  },
  [ButtonPreset.Dark]: {
    bgColor: ColorType.Black,
    bgOpacity: 0.33,
    borderColor: ColorType.White,
    borderOpacity: 0.06,
    borderWidth: 1,
    backdropBlur: 1,
    glow: 0,
    hoverBgOpacity: 0.42,
    hoverBorderOpacity: 0.16,
    hoverGlow: 0,
    clickBgOpacity: 0.5,
    clickBorderOpacity: 0.22,
    clickGlow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.SemiBold,
    glossy: 0,
    customShadow: {
      regular: DARK_BUTTON_SHADOW,
      hover: DARK_BUTTON_SHADOW,
      click: DARK_BUTTON_SHADOW,
    },
  },
  [ButtonPreset.SleekAccent]: {
    bgColor: ColorType.Transparent,
    bgOpacity: 0,
    borderColor: ColorType.Transparent,
    borderOpacity: 0,
    borderWidth: 0,
    backdropBlur: 0,
    glow: 0,
    hoverBgOpacity: 0,
    hoverBorderOpacity: 0,
    hoverGlow: 0,
    clickBgOpacity: 0,
    clickBorderOpacity: 0,
    clickGlow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.SemiBold,
    glossy: 0,
    customShadow: {
      regular: 'inset 0 0 0 1px rgba(102,254,193,.3), 0 20px 50px -20px rgba(0,0,0,.8)',
      hover: 'inset 0 0 0 1px rgba(102,254,193,.3), 0 20px 50px -20px rgba(0,0,0,.8)',
      click: 'inset 0 0 0 1px rgba(102,254,193,.3), 0 20px 50px -20px rgba(0,0,0,.8)',
    },
    containerStyle: {
      backgroundImage: 'radial-gradient(circle at 50% 30%, rgba(102,254,193,.18), rgba(0,55,29,.4) 55%, rgba(0,0,0,.6))',
    },
  },
  [ButtonPreset.ModernPrimary]: {
    bgColor: ColorType.Accent,
    bgOpacity: 0.12,
    borderColor: ColorType.Accent,
    borderOpacity: 0.32,
    borderWidth: 1,
    backdropBlur: 0,
    glow: 0,
    hoverBgOpacity: 0.2,
    hoverBorderOpacity: 0.42,
    hoverGlow: 0,
    clickBgOpacity: 0.26,
    clickBorderOpacity: 0.45,
    clickGlow: 0,
    textColor: ColorType.Accent,
    textWeight: FontWeight.Bold,
  },
  [ButtonPreset.GlowPrimary]: {
    bgColor: ColorType.Accent,
    bgOpacity: 0.1,
    borderColor: ColorType.Accent,
    borderOpacity: 0.4,
    borderWidth: 1,
    backdropBlur: 1,
    glow: 2,
    hoverBgOpacity: 0.16,
    hoverBorderOpacity: 0.55,
    hoverGlow: 3,
    clickBgOpacity: 0.22,
    clickBorderOpacity: 0.6,
    clickGlow: 1,
    textColor: ColorType.Accent,
    textWeight: FontWeight.Bold,
    customShadow: {
      regular:
        '0 0 30px rgba(95,243,184,0.16), inset 0px 1px 1px 0px rgba(255,255,255,0.12)',
      hover:
        '0 0 36px rgba(95,243,184,0.28), inset 0px 1px 1px 0px rgba(255,255,255,0.16)',
      click:
        '0 0 18px rgba(95,243,184,0.14), inset 0px 1px 1px 0px rgba(255,255,255,0.1)',
    },
  },
  [ButtonPreset.TabActive]: {
    bgColor: ColorType.Accent,
    bgOpacity: 0.2,
    borderColor: ColorType.Accent,
    borderOpacity: 0.2,
    borderWidth: 1,
    backdropBlur: 0,
    glow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.Heavy,
    textSize: 18,
  },
  [ButtonPreset.TabInactive]: {
    bgColor: ColorType.Black,
    bgOpacity: 0.12,
    borderColor: ColorType.Accent,
    borderOpacity: 0.12,
    borderWidth: 1,
    backdropBlur: 0,
    glow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.Light,
    textSize: 18,
  },
  [ButtonPreset.Danger]: {
    bgColor: ColorType.White,
    bgOpacity: 0.07,
    borderColor: ColorType.White,
    borderOpacity: 0,
    borderWidth: 0,
    backdropBlur: 3,
    glow: 0,
    hoverBgOpacity: 0.06,
    hoverBorderOpacity: 0,
    hoverGlow: 1,
    clickBgOpacity: 0.06,
    clickBorderOpacity: 0.22,
    clickGlow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.SemiBold,
    customShadow: {
      regular: `inset 0px -2px 6px 0px ${getColorValue(ColorType.Danger, 0.6)}, rgb(255 255 255 / 23%) 0px 1px 1px 0px inset, ${getColorValue(ColorType.Danger, 0.2)} 2px 7px 2px 0px`,
      hover: `inset 0px -5px 12px 0px ${getColorValue(ColorType.Danger, 0.7)}, rgb(255 255 255 / 23%) 0px 1px 1px 0px inset, rgb(255 255 255 / 23%) 0px -1px 1px 0px inset, ${getColorValue(ColorType.Danger, 0.6)} 2px 7px 12px 0px`,
      click: `rgba(255, 255,255, 0.17) 0px 0px 2px 1px inset, 0px 6px 4px 3px ${getColorValue(ColorType.Danger, 0)} inset, 0px -10px 20px 4px ${getColorValue(ColorType.Danger, 0.8)} inset`,
    },
  },
  [ButtonPreset.Embossed]: {
    bgColor: ColorType.Transparent,
    bgOpacity: 0,
    borderColor: ColorType.Transparent,
    borderOpacity: 0,
    borderWidth: 0,
    backdropBlur: 0,
    glow: 0,
    hoverBgOpacity: 0.2,
    hoverBorderOpacity: 0,
    hoverGlow: 0,
    clickBgOpacity: 0.6,
    clickBorderOpacity: 0,
    clickGlow: 0,
    textColor: ColorType.White,
    textWeight: FontWeight.SemiBold,
    customShadow: {
      regular: EMBOSSED_BUTTON_SHADOW,
      hover: EMBOSSED_BUTTON_SHADOW,
      click: EMBOSSED_BUTTON_SHADOW,
    },
  },
};
